// biome-ignore-all lint/correctness/useUniqueElementIds: historic

import Button from '@components/Button/Button';
import EditCardDialogPopover from '@components/Card/EditCardDialogPopover/EditCardDialogPopover';
import { type FieldKind, getFieldTitle } from '@components/Card/EditCardDialogPopover/field-titles';
import EditCardPopover, {
  EditCardControlsSection,
  Panel,
} from '@components/Card/EditCardPopover/EditCardPopover';
import DisableEditIcon from '@components/Icons/DisableEditIcon';
import {
  ButtonsSection,
  ContentSection,
  Scrollable,
  StickyBottom,
} from '@components/Popover/PopoverLayouts';
import WithPlaceholder from '@components/WithPlaceholder/WithPlaceholder';
import { cardContext } from '@context/card-context';
import { CardEditPopoverShellContext } from '@context/card-edit-popover-shell-context';
import { cardFieldEditContext } from '@context/card-field-edit-context';
import { editedCardFieldsContext } from '@context/edited-card-fields-context';
import { getOrCreateContext } from '@context/singleton-manager';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { faLock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DragDropContext, type DropResult, Droppable } from '@hello-pangea/dnd';
import useNode, { type SettableNode } from '@hooks/use-node';
import useResizeObserver from '@hooks/use-resize-observer';
import useSingletonContext from '@hooks/use-singleton-context';
import {
  constructFullName,
  createOrderedCardFields,
  getFieldAttributes,
  isFieldValueValid,
  isOneOfAttributesLinked,
  isURLFieldValueValid,
} from '@modules/card-helpers';
import { hexToRGB, isHexValue } from '@modules/color-helpers';
import { formatUrl } from '@modules/helpers';
import { createPhoneNumberLink } from '@modules/url-helpers';
import { tokens } from '@ui-lib/css';
import { motion } from 'motion/react';
import {
  type FormEvent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CardAddress, CardEmail, CardFields, CardPhoneNumber, CardUrl } from 'types';

import styles from './Card.module.scss';
import CardHeader from './CardHeader/CardHeader';
import type { ImageType } from './CardHeader/utilities';
import CardLoader from './CardLoader';
import PreventOverrideToggle from './EditCardPopover/PreventOverrideToggle';
import AccreditationsEditPopover from './FieldEditPopovers/AccreditationsEditPopover/AccreditationsEditPopover';
import AddressEditPopover from './FieldEditPopovers/AddressEditPopover/AddressEditPopover';
import CompanyEditPopover from './FieldEditPopovers/CompanyEditPopover/CompanyEditPopover';
import DepartmentEditPopover from './FieldEditPopovers/DepartmentEditPopover/DepartmentEditPopover';
import EmailEditPopover from './FieldEditPopovers/EmailEditPopover/EmailEditPopover';
import HeadlineEditPopover from './FieldEditPopovers/HeadlineEditPopover/HeadlineEditPopover';
import JobTitleEditPopover from './FieldEditPopovers/JobTitleEditPopover/JobTitleEditPopover';
import NameEditPopover from './FieldEditPopovers/NameEditPopover/NameEditPopover';
import PhoneNumberEditPopover from './FieldEditPopovers/PhoneNumberEditPopover/PhoneNumberEditPopover';
import UrlEditPopover from './FieldEditPopovers/UrlEditPopover/UrlEditPopover';
import AddressField from './Fields/AddressField';
import EmailField from './Fields/EmailField';
import { OptionalDraggable } from './Fields/OptionalDraggable';
import PhoneNumberField from './Fields/PhoneNumberField';
import UrlField from './Fields/UrlField';
import { shouldShowAccreditationsField, shouldShowTextField } from './utils';

interface CardProps {
  canDeleteFields?: boolean;
  canEditHeader?: boolean; // This is poorly done but it will do for now
  editMode?: boolean;
  createMode?: boolean;
  forwardedRef?: (instance: HTMLElement | null) => void;
  headingNode?: SettableNode<HTMLHeadingElement> | undefined;
  loading?: boolean;
  mode?: 'fit' | 'standalone';
  picturePreview?: ImageType;
}

const outlineContext = getOrCreateContext('card-outline');

// Convert the rem-valued design-system space token into the pixel number the
// popover positioner expects. Assumes the standard 16px root font-size used
// throughout the app.
const SPACE_LARGE_PX = parseFloat(tokens.space.large) * 16;

/**
 * You might be wondering why I don't use forwardRef here, and instead pass the ref as a prop. The reason
 * is that NextJs dynamic components don't play nice with forwarding refs, I think it has something to do
 * with the component being wrapped inside another component to achieve dynamic rendering.
 */
const Card: React.FC<React.PropsWithChildren<CardProps>> = ({
  canDeleteFields = true,
  canEditHeader = true,
  editMode = false,
  createMode = false,
  forwardedRef,
  headingNode,
  loading = false,
  mode = 'standalone',
  picturePreview,
}) => {
  const [outlineContextProps] = useSingletonContext();
  const {
    cardFields,
    cardFieldsDispatch,
    cardColor = '',
    onFieldDelete,
    saveChanges,
  } = useContext(cardContext);
  const { activeId: activeEditFieldId } = useContext(cardFieldEditContext);

  const fullName = constructFullName(cardFields);
  const orderedFields = createOrderedCardFields(cardFields);

  const [size, setSize] = useState<'extra-small' | 'small' | 'regular'>('regular');

  const rgbColor = isHexValue(cardColor) ? hexToRGB(cardColor, { tripletOnly: true }) : cardColor;

  const onSizeChange = useCallback(
    (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        // I provide a slight buffer in between the two width thresholds to avoid flickering
        if (entry.contentBoxSize) {
          if (entry.contentBoxSize[0]) {
            const width = entry.contentBoxSize[0].inlineSize;
            if (width < 260 && size !== 'extra-small') {
              setSize('extra-small');
            } else if (width < 335 && width >= 260 && size !== 'small') {
              setSize('small');
            } else if (width >= 340 && size !== 'regular') {
              setSize('regular');
            }
          }
        } else {
          const width = entry.contentRect.width;
          if (width < 260 && size !== 'extra-small') {
            setSize('extra-small');
          } else if (width < 335 && width >= 260 && size !== 'small') {
            setSize('small');
          } else if (width >= 340 && size !== 'regular') {
            setSize('regular');
          }
        }
      }
    },
    [size],
  );

  const handleDelete = useCallback(
    (id: string) => {
      switch (id) {
        case 'name':
          cardFieldsDispatch({ type: 'REMOVE_NAME' });
          break;
        case 'job_title':
          cardFieldsDispatch({ type: 'REMOVE_JOB_TITLE' });
          break;
        case 'org_name':
          cardFieldsDispatch({ type: 'REMOVE_COMPANY' });
          break;
        case 'department':
          cardFieldsDispatch({ type: 'REMOVE_DEPARTMENT' });
          break;
        case 'accreditations':
          cardFieldsDispatch({ type: 'REMOVE_ALL_ACCREDITATIONS' });
          break;
        case 'headline':
          cardFieldsDispatch({ type: 'REMOVE_HEADLINE' });
          break;
      }

      onFieldDelete?.(id);
    },
    [cardFieldsDispatch, onFieldDelete],
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source } = result;

      // We can detect if we drop the field outside of the droppable area as there is no destination. In this
      // case we want to unlock the ability to change which field is outlined, while also removing the outline
      // from any existing fields since the mouse is outside the droppable area and therefore not over any field.
      if (!destination) {
        outlineContextProps.unlock({ command: 'make-inactive' });
        return;
      }

      // If we didn't end up moving the field then we want the field that was being dragged, i.e. the active one, to
      // remain active, while also allowing other fields to request to be the active field.
      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        outlineContextProps.unlock();
        return;
      }

      cardFieldsDispatch({
        type: 'REORDER_FIELDS',
        value: { from: source.index, to: destination.index },
      });

      saveChanges();

      // If we drop the field and while it's finishing the drop animation (and before onDragEnd is called) we
      // move to another field we want the field outline to move to the field the mouse is now hovering over.
      // This will be the field that made the last request to be activated. While dragging a field our mouse does not
      // enter any other field which means activating the last request is just activating the current field, which is
      // what we want.
      outlineContextProps.unlock({ command: 'activate-last-request' });
    },
    [cardFieldsDispatch, outlineContextProps, saveChanges],
  );

  const handleDragStart = useCallback(() => {
    outlineContextProps.lock();
  }, [outlineContextProps]);

  const resizeRef = useResizeObserver(onSizeChange);

  // Containers are used around the fields in order to add margins without messing up the outline styling
  // (when in edit mode)
  return (
    <outlineContext.Provider value={outlineContextProps}>
      <div ref={resizeRef} className={styles['card-measurer']} data-mode={mode}>
        {loading && (
          <div className={styles['card-loader-container']} data-mode={mode}>
            <CardLoader mode={mode} />
          </div>
        )}
        {!loading && (
          <div className={styles['card-container']} style={{ '--card-color': rgbColor }}>
            <section
              className={styles['card']}
              data-edit-mode={editMode}
              ref={forwardedRef}
              data-size={size}
              data-mode={mode}
            >
              <CardHeader
                node={headingNode}
                isPreset={false}
                createMode={createMode && canEditHeader}
                editMode={editMode && canEditHeader}
                mode={mode}
                layout={cardFields.layout}
                coverPhotoImg={cardFields.coverPhotoImg}
                profilePictureImg={cardFields.profilePictureImg}
                companyLogoImg={cardFields.companyLogoImg}
                picturePreview={picturePreview}
              />
              <div className={styles['card-body']}>
                <div className={styles['user-details']}>
                  {shouldShowTextField(fullName, activeEditFieldId === 'name') && (
                    <div className={styles['user-name-container']}>
                      <CardField
                        editMode={editMode}
                        editUI={<NameEditPopover />}
                        canDelete={true}
                        id="name"
                        fieldKind="name"
                        onDelete={() => handleDelete('name')}
                      >
                        <WithPlaceholder
                          placeholderText="Name"
                          show={fullName === '' && activeEditFieldId === 'name'}
                        >
                          {(props) => (
                            <>
                              <div className={styles['user-name']} {...props}>
                                {fullName}
                              </div>
                              {cardFields.pronouns && (
                                <small className={styles['user-pronouns']}>
                                  ({cardFields.pronouns})
                                </small>
                              )}
                            </>
                          )}
                        </WithPlaceholder>
                      </CardField>
                    </div>
                  )}

                  {shouldShowTextField(cardFields.jobTitle, activeEditFieldId === 'job_title') && (
                    <div className={styles['user-job-title-container']}>
                      <CardField
                        editMode={editMode}
                        editUI={
                          <JobTitleEditPopover
                            field={cardFields.jobTitle || ''}
                            onChange={(value) =>
                              cardFieldsDispatch({
                                type: 'UPDATE_JOB_TITLE',
                                value,
                              })
                            }
                            linked={cardFields.linkedAttributes.includes('job_title')}
                          />
                        }
                        canDelete={canDeleteFields}
                        onDelete={() => handleDelete('job_title')}
                        id="job_title"
                        fieldKind="jobTitle"
                      >
                        <WithPlaceholder
                          placeholderText="Job title"
                          show={cardFields.jobTitle === '' && activeEditFieldId === 'job_title'}
                        >
                          {(props) => (
                            <div className={styles['user-job-title']} {...props}>
                              {cardFields.jobTitle}
                            </div>
                          )}
                        </WithPlaceholder>
                      </CardField>
                    </div>
                  )}
                  {shouldShowTextField(
                    cardFields.department,
                    activeEditFieldId === 'department',
                  ) && (
                    <div className={styles['user-department-container']}>
                      <CardField
                        editMode={editMode}
                        editUI={
                          <DepartmentEditPopover
                            field={cardFields.department || ''}
                            onChange={(value) =>
                              cardFieldsDispatch({
                                type: 'UPDATE_DEPARTMENT',
                                value,
                              })
                            }
                            linked={cardFields.linkedAttributes.includes('department')}
                          />
                        }
                        canDelete={canDeleteFields}
                        onDelete={() => handleDelete('department')}
                        id="department"
                        fieldKind="department"
                      >
                        <WithPlaceholder
                          placeholderText="Department"
                          show={cardFields.department === '' && activeEditFieldId === 'department'}
                        >
                          {(props) => (
                            <div className={styles['user-department']} {...props}>
                              {cardFields.department}
                            </div>
                          )}
                        </WithPlaceholder>
                      </CardField>
                    </div>
                  )}
                  {shouldShowTextField(cardFields.orgName, activeEditFieldId === 'org_name') && (
                    <div className={styles['user-company-container']}>
                      <CardField
                        editMode={editMode}
                        editUI={
                          <CompanyEditPopover
                            field={cardFields.orgName || ''}
                            onChange={(value) =>
                              cardFieldsDispatch({
                                type: 'UPDATE_COMPANY',
                                value,
                              })
                            }
                            linked={cardFields.linkedAttributes.includes('org_name')}
                          />
                        }
                        canDelete={canDeleteFields}
                        onDelete={() => handleDelete('org_name')}
                        id="org_name"
                        fieldKind="company"
                      >
                        <WithPlaceholder
                          placeholderText="Company name"
                          show={cardFields.orgName === '' && activeEditFieldId === 'org_name'}
                        >
                          {(props) => (
                            <div className={styles['user-company']} {...props}>
                              {cardFields.orgName}
                            </div>
                          )}
                        </WithPlaceholder>
                      </CardField>
                    </div>
                  )}
                  {!!cardFields.accreditations &&
                    shouldShowAccreditationsField(
                      cardFields.accreditations,
                      activeEditFieldId === 'accreditations',
                    ) && (
                      <div className={styles['user-accreditations-container']}>
                        <CardField
                          editMode={editMode}
                          editUI={
                            <AccreditationsEditPopover
                              field={cardFields.accreditations}
                              onChange={(newAccreditations) =>
                                cardFieldsDispatch({
                                  type: 'UPDATE_ALL_ACCREDITATIONS',
                                  value: newAccreditations,
                                })
                              }
                            />
                          }
                          canDelete={canDeleteFields}
                          onDelete={() => handleDelete('accreditations')}
                          id="accreditations"
                          fieldKind="accreditation"
                          positionDeps={[cardFields.accreditations.length]}
                        >
                          <ul className={styles['user-accreditations']}>
                            {cardFields.accreditations.map((accreditation, i) => (
                              // biome-ignore lint/suspicious/noArrayIndexKey: historic
                              <li className={styles['accreditation']} key={i}>
                                {accreditation}
                              </li>
                            ))}
                          </ul>
                        </CardField>
                      </div>
                    )}
                  {shouldShowTextField(cardFields.headline, activeEditFieldId === 'headline') && (
                    <div className={styles['user-headline-container']}>
                      <CardField
                        editMode={editMode}
                        editUI={
                          <HeadlineEditPopover
                            field={cardFields.headline || ''}
                            onChange={(value) =>
                              cardFieldsDispatch({
                                type: 'UPDATE_HEADLINE',
                                value,
                              })
                            }
                          />
                        }
                        canDelete={canDeleteFields}
                        onDelete={() => handleDelete('headline')}
                        id="headline"
                        fieldKind="headline"
                      >
                        <WithPlaceholder
                          placeholderText="Headline"
                          show={cardFields.headline === '' && activeEditFieldId === 'headline'}
                        >
                          {(props) => (
                            <div className={styles['user-headline']} {...props}>
                              {cardFields.headline}
                            </div>
                          )}
                        </WithPlaceholder>
                      </CardField>
                    </div>
                  )}
                </div>
                {orderedFields.length > 0 && (
                  <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
                    <div className={styles['card-items-container']}>
                      <Droppable droppableId="card-fields">
                        {(provided, snapshot) => (
                          <ul
                            className={styles['card-items']}
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            data-dragging-over={snapshot.isDraggingOver}
                          >
                            {orderedFields.map((field) => {
                              switch (field.type) {
                                case 'email':
                                  return (
                                    <li key={field.value.id}>
                                      <CardItem
                                        href={`mailto:${field.value.value}`}
                                        editMode={editMode}
                                        type="email"
                                        field={field.value}
                                        size={size}
                                      />
                                    </li>
                                  );
                                case 'phoneNumber':
                                  return (
                                    <li key={field.value.id}>
                                      <CardItem
                                        href={createPhoneNumberLink(
                                          field.value.value,
                                          field.value.extension,
                                        )}
                                        editMode={editMode}
                                        type="phone"
                                        field={field.value}
                                        size={size}
                                      />
                                    </li>
                                  );
                                case 'url':
                                  return (
                                    <li key={field.value.id}>
                                      <CardItem
                                        href={formatUrl(field.value.type, field.value.value)}
                                        openInNewWindow
                                        editMode={editMode}
                                        type="url"
                                        field={field.value}
                                        size={size}
                                      />
                                    </li>
                                  );
                                case 'address':
                                  return (
                                    <li key={field.value.id}>
                                      <CardItem
                                        href={`https://maps.google.com/?q=${field.value.value}`}
                                        openInNewWindow
                                        editMode={editMode}
                                        type="address"
                                        field={field.value}
                                        size={size}
                                      />
                                    </li>
                                  );
                              }
                            })}
                            {provided.placeholder}
                          </ul>
                        )}
                      </Droppable>
                    </div>
                  </DragDropContext>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </outlineContext.Provider>
  );
};

Card.displayName = 'Card';

type DeleteFieldProps =
  | {
      canDelete: true;
      onDelete: () => void;
    }
  | {
      canDelete: false;
    };

type CardFieldProps = {
  editMode: boolean;
  editUI: React.ReactElement;
  isValid?: boolean;
  id: string;
  draggable?: boolean;
  validator?: () => boolean;
  positionDeps?: React.DependencyList;
  fieldKind?: FieldKind;
} & DeleteFieldProps;

const CardField: React.FC<React.PropsWithChildren<CardFieldProps>> = (props) => {
  const { lockedFields, canLock, cardFields } = useContext(cardContext);
  const {
    activeId,
    requestToMakeActive,
    makeInactive,
    unlock: unlockOutline,
  } = useContext(outlineContext);
  const {
    activeId: activeEditPopoverId,
    requestToMakeActive: requestToEdit,
    makeInactive: requestToHideEdit,
  } = useContext(cardFieldEditContext);
  const [fieldNode, fieldRef] = useNode<HTMLDivElement | HTMLButtonElement>();
  const [fieldBtnNode, fieldBtnRef] = useNode<HTMLDivElement | HTMLButtonElement>();

  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // This allows us to momentarily keep an outline active when the user moves there mouse away but still
  // properly react if in that instant they move their mouse back to the field or onto a different field.
  // This enables the outline to smoothly transition between fields or fade away if the user is no longer
  // hovering, instead of having the outline instantly appear/dissapear.
  const shouldMakeInactiveRef = useRef(false);

  const isActive = activeId == props.id;
  const showEditPopover = activeEditPopoverId === props.id;
  const index = cardFields.fieldOrder.findIndex((id) => id === props.id);
  const isLocked = lockedFields.includes(props.id);

  function handleMouseEnterUnlockedField() {
    if (props.editMode) {
      shouldMakeInactiveRef.current = false;
      requestToMakeActive(props.id);
      setIsHovering(true);
    }
  }

  function handleMouseEnterLockedField() {
    if (props.editMode) {
      shouldMakeInactiveRef.current = false;
      makeInactive();
      setIsHovering(true);
    }
  }

  function handleFocusUnlockedField() {
    if (props.editMode) {
      requestToMakeActive(props.id);
      setIsFocused(true);
    }
  }

  function handleFocusLockedField() {
    if (props.editMode) {
      makeInactive();
      setIsFocused(true);
    }
  }

  function handleBlurUnlockedField() {
    if (props.editMode) {
      makeInactive(props.id);
      setIsFocused(false);
    }
  }

  function handleBlurLockedField() {
    if (props.editMode) {
      makeInactive(props.id);
      setIsFocused(false);
    }
  }

  function handleMouseLeaveLockedField() {
    if (props.editMode) {
      setIsHovering(false);
    }
  }

  function handleMouseLeaveUnlockedField() {
    if (props.editMode) {
      shouldMakeInactiveRef.current = true;
      setIsHovering(false);

      setTimeout(() => {
        if (shouldMakeInactiveRef.current) {
          makeInactive(props.id);
        }
      }, 300);
    }
  }

  function handleFieldClick() {
    unlockOutline({ command: 'activate-last-request' });
    requestToEdit(props.id, 'update');
  }

  const handleClose = useCallback(() => {
    requestToHideEdit(props.id);
    fieldBtnNode?.focus();
  }, [requestToHideEdit, props.id, fieldBtnNode]);
  // a non draggable version of the field, if needed
  const nonDraggableField = (
    <div
      className={styles['card-field']}
      data-id={props.id}
      data-is-locked={isLocked}
      data-can-lock={canLock}
      data-edit-mode={props.editMode}
      onMouseEnter={handleMouseEnterUnlockedField}
      onMouseLeave={handleMouseLeaveUnlockedField}
      ref={fieldRef}
    >
      <button
        className={styles['card-field-btn']}
        onFocus={handleFocusUnlockedField}
        onBlur={handleBlurUnlockedField}
        onKeyDown={(event) => {
          // This must be keyDown otherwise submitting the form in the edit popover will reopen the edit popover
          if (event.key === 'Enter') {
            requestToEdit(props.id, 'update');
          }
        }}
        onClick={handleFieldClick}
        data-id={props.id}
        data-is-locked={isLocked}
        data-can-lock={canLock}
        data-edit-mode={props.editMode}
        ref={fieldBtnRef}
      >
        {(isActive || showEditPopover) && (
          <>
            <motion.div className={styles['card-field-outline']} layoutId="outline"></motion.div>
            {isActive && (
              <motion.div
                className={styles['edit-icon']}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: { duration: 0.2, delay: 0.3 },
                }}
              >
                {(!isLocked || canLock) && <FontAwesomeIcon icon={faEdit} />}
              </motion.div>
            )}
          </>
        )}

        {isLocked && (
          <motion.div
            className={styles['lock-icon']}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { duration: 0.2, delay: isActive ? 0.3 : 0 },
            }}
          >
            <DisableEditIcon />
          </motion.div>
        )}
        {props.children}
      </button>
    </div>
  );
  if (!props.editMode) {
    return <>{props.children}</>;
  } else if (isLocked && !canLock) {
    return (
      <>
        <OptionalDraggable
          draggableId={props.id}
          index={index}
          draggable={props.draggable}
          fallback={nonDraggableField}
        >
          {(provided, snapshot) => {
            return (
              <div
                className={styles['card-field']}
                data-id={props.id}
                data-is-locked={true}
                data-can-lock={canLock}
                data-edit-mode={props.editMode}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                onMouseEnter={handleMouseEnterLockedField}
                onMouseLeave={handleMouseLeaveLockedField}
                onFocus={handleFocusLockedField}
                onBlur={handleBlurLockedField}
                ref={provided.innerRef}
              >
                <motion.div
                  className={styles['lock-icon']}
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: { duration: 0.2, delay: isActive ? 0.3 : 0 },
                  }}
                >
                  <FontAwesomeIcon icon={faLock} />
                </motion.div>
                {props.children}

                {snapshot.isDragging && (
                  <div className={styles['card-field-outline']} data-floating></div>
                )}

                {(isHovering || isFocused || snapshot.isDragging) && (
                  <motion.div
                    className={styles['drag-to-reorder-handle']}
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: {
                        duration: 0.2,
                        delay: isActive ? 0.3 : 0,
                      },
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512">
                      <path d="M32 96C32 78.33 46.33 64 64 64C81.67 64 96 78.33 96 96C96 113.7 81.67 128 64 128C46.33 128 32 113.7 32 96zM32 256C32 238.3 46.33 224 64 224C81.67 224 96 238.3 96 256C96 273.7 81.67 288 64 288C46.33 288 32 273.7 32 256zM96 416C96 433.7 81.67 448 64 448C46.33 448 32 433.7 32 416C32 398.3 46.33 384 64 384C81.67 384 96 398.3 96 416zM160 96C160 78.33 174.3 64 192 64C209.7 64 224 78.33 224 96C224 113.7 209.7 128 192 128C174.3 128 160 113.7 160 96zM224 256C224 273.7 209.7 288 192 288C174.3 288 160 273.7 160 256C160 238.3 174.3 224 192 224C209.7 224 224 238.3 224 256zM160 416C160 398.3 174.3 384 192 384C209.7 384 224 398.3 224 416C224 433.7 209.7 448 192 448C174.3 448 160 433.7 160 416z" />
                    </svg>
                  </motion.div>
                )}
              </div>
            );
          }}
        </OptionalDraggable>
      </>
    );
  } else {
    // The edit icon is outside the card-field outline, otherwise it gets distorted by the outline
    // as it morphs when changing which field is highlighted
    return (
      <>
        <OptionalDraggable
          draggable={props.draggable}
          draggableId={props.id}
          index={index}
          fallback={nonDraggableField}
        >
          {(provided, snapshot) => {
            return (
              <div
                className={styles['card-field']}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                onMouseEnter={handleMouseEnterUnlockedField}
                onMouseLeave={handleMouseLeaveUnlockedField}
                onFocus={handleFocusUnlockedField}
                onBlur={handleBlurUnlockedField}
                onKeyDown={(event) => {
                  // This must be keyDown otherwise submitting the form in the edit popover will reopen the edit popover
                  if (event.key === 'Enter') {
                    requestToEdit(props.id, 'update');
                  }
                }}
                onClick={handleFieldClick}
                tabIndex={0}
                role="button"
                ref={(element) => {
                  provided.innerRef(element);
                  fieldRef(element);
                  fieldBtnRef(element);
                }}
              >
                <div
                  className={styles['card-field-btn']}
                  data-id={props.id}
                  data-is-locked={isLocked}
                  data-can-lock={canLock}
                  data-edit-mode={props.editMode}
                >
                  {(isActive || showEditPopover) && !snapshot.isDragging && (
                    <>
                      <motion.div
                        className={styles['card-field-outline']}
                        layoutId="outline"
                      ></motion.div>
                      {isActive && (
                        <motion.div
                          className={styles['edit-icon']}
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: 1,
                            transition: { duration: 0.2, delay: 0.3 },
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </motion.div>
                      )}
                    </>
                  )}
                  {snapshot.isDragging && (
                    <div className={styles['card-field-outline']} data-floating></div>
                  )}

                  {isLocked && (
                    <motion.div
                      className={styles['lock-icon']}
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: 1,
                        transition: {
                          duration: 0.2,
                          delay: isActive ? 0.3 : 0,
                        },
                      }}
                    >
                      <DisableEditIcon />
                    </motion.div>
                  )}
                  {props.children}
                </div>

                {(isActive || snapshot.isDragging) && (
                  <motion.div
                    className={styles['drag-to-reorder-handle']}
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: {
                        duration: 0.2,
                        delay: isActive ? 0.3 : 0,
                      },
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512">
                      <path d="M32 96C32 78.33 46.33 64 64 64C81.67 64 96 78.33 96 96C96 113.7 81.67 128 64 128C46.33 128 32 113.7 32 96zM32 256C32 238.3 46.33 224 64 224C81.67 224 96 238.3 96 256C96 273.7 81.67 288 64 288C46.33 288 32 273.7 32 256zM96 416C96 433.7 81.67 448 64 448C46.33 448 32 433.7 32 416C32 398.3 46.33 384 64 384C81.67 384 96 398.3 96 416zM160 96C160 78.33 174.3 64 192 64C209.7 64 224 78.33 224 96C224 113.7 209.7 128 192 128C174.3 128 160 113.7 160 96zM224 256C224 273.7 209.7 288 192 288C174.3 288 160 273.7 160 256C160 238.3 174.3 224 192 224C209.7 224 224 238.3 224 256zM160 416C160 398.3 174.3 384 192 384C209.7 384 224 398.3 224 416C224 433.7 209.7 448 192 448C174.3 448 160 433.7 160 416z" />
                    </svg>
                  </motion.div>
                )}
              </div>
            );
          }}
        </OptionalDraggable>

        {props.canDelete && (
          <EditCardFieldPopover
            id={props.id}
            targetNode={fieldNode}
            show={showEditPopover}
            onClose={handleClose}
            onDelete={props.onDelete}
            canDelete={true}
            isValid={props.isValid}
            validator={props.validator}
            positionDeps={props.positionDeps}
            fieldKind={props.fieldKind}
          >
            {props.editUI}
          </EditCardFieldPopover>
        )}
        {!props.canDelete && (
          <EditCardFieldPopover
            id={props.id}
            targetNode={fieldNode}
            show={showEditPopover}
            onClose={handleClose}
            canDelete={false}
            isValid={props.isValid}
            validator={props.validator}
            positionDeps={props.positionDeps}
            fieldKind={props.fieldKind}
          >
            {props.editUI}
          </EditCardFieldPopover>
        )}
      </>
    );
  }
};

type EditCardFieldPopoverProps = {
  id: string;
  targetNode: HTMLElement | undefined;
  show: boolean;
  onClose: () => void;
  isValid?: boolean;
  validator?: () => boolean;
  positionDeps?: React.DependencyList;
  fieldKind?: FieldKind;
} & DeleteFieldProps;

const EditCardFieldPopover: React.FC<React.PropsWithChildren<EditCardFieldPopoverProps>> = memo(
  (props) => {
    const { mode: shellMode } = useContext(CardEditPopoverShellContext);
    const {
      cardFields,
      cardFieldsDispatch,
      saveChanges,
      rollbackChanges,
      lockedFields,
      toggleFieldLock,
      canLock,
    } = useContext(cardContext);
    const [editedAttributes, setEditedAttributes] = useState<string[]>([]);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [showUnlinkWarning, setShowUnlinkWarning] = useState(false);
    const cardFieldsRef = useRef<CardFields>(undefined);
    const isLocked = lockedFields.includes(props.id);
    const originalLockStateRef = useRef<boolean>(undefined);
    const hasFieldStateChanged = cardFieldsRef.current && cardFields !== cardFieldsRef.current;
    const hasLockStateChanged =
      originalLockStateRef.current !== undefined && isLocked !== originalLockStateRef.current;
    const fieldsChanged = hasFieldStateChanged || hasLockStateChanged;
    const numEditedLinkedAttributes = useMemo(() => {
      let count = 0;

      editedAttributes.forEach((attribute) => {
        if (cardFields.linkedAttributes.includes(attribute)) {
          count++;
        }
      });

      return count;
    }, [editedAttributes, cardFields.linkedAttributes]);

    const unlinkExplanation = useMemo(() => {
      if (numEditedLinkedAttributes) {
        if (numEditedLinkedAttributes > 1) {
          return `You have edited ${numEditedLinkedAttributes} linked fields. By clicking Save and unlink these fields will be unlinked and your changes will be saved.`;
        } else {
          return 'You have edited a linked field. By clicking Save and unlink this field will be unlinked and your changes will be saved.';
        }
      } else if (hasLockStateChanged && isLocked === false) {
        return 'Unlocking a field also unlinks it.';
      }
    }, [isLocked, hasLockStateChanged, numEditedLinkedAttributes]);

    useEffect(() => {
      if (props.show && originalLockStateRef.current === undefined) {
        originalLockStateRef.current = isLocked;
      } else if (!props.show) {
        originalLockStateRef.current = undefined;
      }
    }, [props.show, isLocked]);

    useEffect(() => {
      if (props.show && !cardFieldsRef.current) {
        cardFieldsRef.current = cardFields;
      } else if (!props.show) {
        cardFieldsRef.current = undefined;
      }
    }, [props.show, cardFields]);

    useEffect(() => {
      if (!props.show) {
        setEditedAttributes([]);
        setShowUnlinkWarning(false);
        setShowDeleteWarning(false);
      }
    }, [props.show]);

    function handleSubmit(e: FormEvent) {
      e.preventDefault();

      const attributesToCheck = getFieldAttributes(props.id);
      const isFieldLinked = isOneOfAttributesLinked(cardFields.linkedAttributes, attributesToCheck);

      // Check if a linked attribute has been edited. If so we want to warn the user
      // before they accidentally unlink it.
      if (
        numEditedLinkedAttributes ||
        (isFieldLinked && hasLockStateChanged && isLocked === false)
      ) {
        setShowUnlinkWarning(true);
      } else {
        saveChanges();
        props.onClose();
      }
    }

    function handleCancel() {
      rollbackChanges();
      toggleFieldLock(props.id, originalLockStateRef.current);
      props.onClose();
    }

    function handleDelete() {
      if (props.canDelete) {
        props.onDelete();
      }
      cardFieldsDispatch({ type: 'UNLINK_ATTRIBUTES', ids: editedAttributes });
      saveChanges();
      props.onClose();
    }

    function handleClosePopover() {
      rollbackChanges();
      toggleFieldLock(props.id, originalLockStateRef.current);
      props.onClose();
    }

    function handleEditAttribute(id: string) {
      const attributeSet = new Set([...editedAttributes, id]);

      setEditedAttributes([...attributeSet]);
    }

    function handleCancelUnlink() {
      handleClosePopover();
    }

    function handleConfirmUnlink() {
      // This isn't very nice. I need to manually map the field IDs to the attributes and unlink the attributes
      // if the lock is being removed. This component shouldn't need to know how IDs map to attributes but this Card component
      // has become a bit of a mess and this is the only place where we have the info on whether the lock state has changed
      // and which attributes have been edited.
      if (hasLockStateChanged && isLocked === false) {
        const attributesToUnlink = getFieldAttributes(props.id);
        cardFieldsDispatch({ type: 'UNLINK_ATTRIBUTES', ids: attributesToUnlink });
      } else if (editedAttributes) {
        cardFieldsDispatch({ type: 'UNLINK_ATTRIBUTES', ids: editedAttributes });
      }

      saveChanges();
      props.onClose();
    }

    const isFieldValid = props.validator?.() ?? true;

    const legacyBodyContent = (
      <>
        <div className={styles['edit-card-field-popover']}>{props.children}</div>
        {canLock && (
          // minor spacing, no need for a css class
          <div style={{ padding: '18px 0px 12px 0px' }}>
            <Panel variant="tight">
              <PreventOverrideToggle
                isLocked={isLocked}
                onLockToggle={() => toggleFieldLock(props.id)}
              />
            </Panel>
          </div>
        )}
      </>
    );

    const dialogBodyContent = (
      <>
        {props.children}
        {canLock && (
          <div style={{ paddingTop: '1rem' }}>
            <Panel variant="tight">
              <PreventOverrideToggle
                isLocked={isLocked}
                onLockToggle={() => toggleFieldLock(props.id)}
              />
            </Panel>
          </div>
        )}
      </>
    );

    if (shellMode === 'dialog') {
      return (
        <editedCardFieldsContext.Provider
          value={{ editedAttributes, onEditAttribute: handleEditAttribute }}
        >
          <EditCardDialogPopover
            fieldType="field"
            title={getFieldTitle(props.fieldKind)}
            targetNode={props.targetNode}
            show={props.show}
            onClose={handleClosePopover}
            drawFocus={fieldsChanged}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            canSubmit={fieldsChanged && isFieldValid}
            canDelete={props.canDelete}
            onDeletePress={() => setShowDeleteWarning(true)}
            onDelete={handleDelete}
            onCancelDeleteWarning={() => setShowDeleteWarning(false)}
            showDeleteWarning={showDeleteWarning}
            showUnlinkWarning={showUnlinkWarning}
            onCancelUnlink={handleCancelUnlink}
            onConfirmUnlink={handleConfirmUnlink}
            unlinkWarningMessage={unlinkExplanation}
            unlinking={false}
            positionDeps={props.positionDeps}
            positionerOptions={{
              adaptive: false,
              alignY: 'cover',
              yPreference: 'largest',
              // `space.large` (default 2rem = 32px) keeps the popover a
              // large-padding step away from the card it edits (which in
              // the customize-card onboarding sits inside the phone preview).
              offsetX: SPACE_LARGE_PX,
              xPreference: 'right',
              bindToViewport: true,
            }}
          >
            {dialogBodyContent}
          </EditCardDialogPopover>
        </editedCardFieldsContext.Provider>
      );
    }

    return (
      <editedCardFieldsContext.Provider
        value={{ editedAttributes, onEditAttribute: handleEditAttribute }}
      >
        <EditCardPopover
          fieldType="field"
          targetNode={props.targetNode}
          show={props.show}
          onClose={handleClosePopover}
          drawFocus={fieldsChanged}
          onCancelDeleteWarning={() => setShowDeleteWarning(false)}
          showDeleteWarning={showDeleteWarning}
          onDelete={handleDelete}
          onSubmit={handleSubmit}
          positionDeps={props.positionDeps}
          showUnlinkWarning={showUnlinkWarning}
          onCancelUnlink={handleCancelUnlink}
          onConfirmUnlink={handleConfirmUnlink}
          unlinkWarningMessage={unlinkExplanation}
          positionerOptions={{
            adaptive: false,
            alignY: 'cover',
            yPreference: 'largest',
            offsetX: 8,
            xPreference: 'right',
            bindToViewport: true,
          }}
        >
          <Scrollable>
            <ContentSection>{legacyBodyContent}</ContentSection>
          </Scrollable>

          <StickyBottom>
            <ButtonsSection>
              <EditCardControlsSection
                canDelete={props.canDelete}
                onDeletePress={() => setShowDeleteWarning(true)}
              >
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!fieldsChanged || !isFieldValid}
                  submit
                >
                  Save
                </Button>
              </EditCardControlsSection>
            </ButtonsSection>
          </StickyBottom>
        </EditCardPopover>
      </editedCardFieldsContext.Provider>
    );
  },
);

EditCardFieldPopover.displayName = 'EditCardFieldPopover';

export type CardField =
  | {
      type: 'phone';
      field: CardPhoneNumber;
    }
  | {
      type: 'email';
      field: CardEmail;
    }
  | {
      type: 'address';
      field: CardAddress;
    }
  | {
      type: 'url';
      field: CardUrl;
    };

type CardItemProps = {
  href: string;
  openInNewWindow?: boolean;
  editMode: boolean;
  size?: 'regular' | 'small' | 'extra-small';
} & CardField;

const CardItem: React.FC<React.PropsWithChildren<CardItemProps>> = (props) => {
  const {
    cardFields,
    cardFieldsDispatch,
    onFieldDelete,
    interactive = true,
  } = useContext(cardContext);
  const { mode } = useContext(cardFieldEditContext);
  const { href, openInNewWindow = false, editMode } = props;

  function renderPopoverContents() {
    switch (props.type) {
      case 'address': {
        return (
          <AddressEditPopover
            field={props.field}
            onChange={(updatedAddress) =>
              cardFieldsDispatch({
                type: 'UPDATE_ADDRESS',
                value: updatedAddress,
                id: props.field.id,
              })
            }
            linked={cardFields.linkedAttributes.includes(props.field.id)}
            mode={mode}
          />
        );
      }
      case 'phone': {
        return (
          <PhoneNumberEditPopover
            field={props.field}
            onChange={(updatedPhoneNumber) =>
              cardFieldsDispatch({
                type: 'UPDATE_PHONE_NUMBER',
                value: updatedPhoneNumber,
                id: props.field.id,
              })
            }
            linked={cardFields.linkedAttributes.includes(props.field.id)}
            mode={mode}
          />
        );
      }
      case 'email': {
        return (
          <EmailEditPopover
            field={props.field}
            onChange={(updatedEmail) =>
              cardFieldsDispatch({
                type: 'UPDATE_EMAIL',
                value: updatedEmail,
                id: props.field.id,
              })
            }
            linked={cardFields.linkedAttributes.includes(props.field.id)}
            mode={mode}
          />
        );
      }
      case 'url': {
        return (
          <UrlEditPopover
            field={props.field}
            onChange={(updatedUrl) =>
              cardFieldsDispatch({
                type: 'UPDATE_URL',
                value: updatedUrl,
                id: props.field.id,
              })
            }
            linked={cardFields.linkedAttributes.includes(props.field.id)}
            mode={mode}
          />
        );
      }
    }
  }

  function handleDelete() {
    switch (props.type) {
      case 'phone':
        cardFieldsDispatch({ type: 'REMOVE_PHONE_NUMBER', id: props.field.id });
        break;
      case 'email':
        cardFieldsDispatch({ type: 'REMOVE_EMAIL', id: props.field.id });
        break;
      case 'url':
        cardFieldsDispatch({ type: 'REMOVE_URL', id: props.field.id });
        break;
      case 'address':
        cardFieldsDispatch({ type: 'REMOVE_ADDRESS', id: props.field.id });
        break;
    }

    onFieldDelete?.(props.field.id);
  }

  function renderCardItemContents() {
    switch (props.type) {
      case 'url':
        return (
          <UrlField
            type={props.field.type}
            title={props.field.title}
            size={props.size}
            interactive={interactive}
          />
        );
      case 'address':
        return (
          <AddressField address={props.field.value} label={props.field.label} size={props.size} />
        );
      case 'email':
        return (
          <EmailField
            email={props.field.value}
            label={props.field.label}
            size={props.size}
            interactive={interactive}
          />
        );
      case 'phone':
        return (
          <PhoneNumberField
            number={props.field.value}
            extension={props.field.extension}
            label={props.field.label}
            size={props.size}
            interactive={interactive}
          />
        );
    }
  }

  if (editMode) {
    return (
      <CardField
        editMode={editMode}
        editUI={renderPopoverContents()}
        onDelete={handleDelete}
        canDelete={true}
        id={props.field.id}
        fieldKind={props.type}
        draggable
        validator={() => {
          if (props.type === 'url') {
            return isURLFieldValueValid(props.field.type, props.field.value) && !!props.field.title;
          } else {
            return isFieldValueValid(props.type, cardFields, props.field.id);
          }
        }}
      >
        <div className={styles['card-item']} data-edit-mode>
          {renderCardItemContents()}
        </div>
      </CardField>
    );
  } else if (interactive) {
    return (
      <a
        className={styles['card-item']}
        href={href}
        target={openInNewWindow ? '_blank' : undefined}
        rel="noreferrer"
        data-is-interactive={interactive}
      >
        {renderCardItemContents()}
      </a>
    );
  } else {
    // If we have set the CardItem to have no UI changes upon interaction
    return <div className={styles['card-item']}>{renderCardItemContents()}</div>;
  }
};
CardItem.displayName = 'CardItem';

export default Card;
